import { faChartLine, faTableList } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Accordion, Center, Group, Stack } from '@mantine/core'
import { OverviewColumns } from 'App/Map/PanelDetails/OverviewCharts/OverviewColumns'
import { Button } from 'components/Button/Button'
import type { TColumn } from 'components/SummaryTable/SummaryTable.types'
import { translate } from 'i18n/i18n'
import { isEqual } from 'lodash'
import { memo, useCallback } from 'react'
import { colors } from 'settings/colors'
import { detailsPanelStore } from 'stores/detailsPanelStore/detailsPanelStore'
import { setSelectedFieldAsset } from 'utils/setSelectedFieldAsset/setSelectedFieldAsset'
import { MenuWithVisibility } from './MenuWIthVisibility'
import type { CurrentView, TMenuColumn, TPointFlowRate, TValueGroupLabel } from './types'

const PropertyAccordion = ({
  propertyName,
  propertyId,
  children,
  layer,
  selectedValueGroupsArray,
  currentView,
  insectIds,
  flowRates,
  timestamp,
  visibleColumns,
  onVisibleColumnsChange,
  isSouthernHemisphere,
  columns,
  isWideScreen,
}: {
  propertyName: string
  propertyId: number
  children: React.ReactNode
  layer: string
  selectedValueGroupsArray: TValueGroupLabel[]
  currentView: CurrentView
  insectIds: number[]
  flowRates: TPointFlowRate[]
  timestamp: string
  visibleColumns: Record<string, boolean>
  onVisibleColumnsChange: (columns: Record<string, boolean>) => void
  isSouthernHemisphere: boolean
  columns: TMenuColumn[]
  isWideScreen: boolean
}) => {
  const handleGraphsClick = useCallback(() => {
    detailsPanelStore.setState((s) => ({ ...s, selectedTab: 'GRAPHS' }))

    setSelectedFieldAsset({ property: propertyId })
  }, [propertyId])

  const handleTablesClick = useCallback(() => {
    detailsPanelStore.setState((s) => ({ ...s, selectedTab: 'TABLES' }))

    setSelectedFieldAsset({ property: propertyId })
  }, [propertyId])

  const handleToggleColumn = (columnKey: string) => {
    const newVisibleColumns = {
      ...visibleColumns,
      [columnKey]: visibleColumns[columnKey] === false ? true : false,
    }

    onVisibleColumnsChange(newVisibleColumns)
  }

  const handleViewAll = () => {
    const allColumns = OverviewColumns({
      view: layer.toLowerCase(),
      selectedValueGroupsArray,
      currentView,
      insectIds,
      pointsFlowRates: flowRates,
      timestamp,
      isSouthernHemisphere,
      propertyId,
    })

    const newVisibleColumns = allColumns.reduce((acc: Record<string, boolean>, col: TColumn) => {
      acc[col.key] = true

      return acc
    }, {})

    onVisibleColumnsChange(newVisibleColumns)
  }

  return (
    <Accordion.Item value={propertyName} key={propertyName}>
      {isWideScreen ? (
        <>
          <Center>
            <Accordion.Control>{propertyName}</Accordion.Control>
            <Button
              variant="link"
              onClick={handleGraphsClick}
              leftIcon={<FontAwesomeIcon icon={faChartLine} color={colors.primary} />}
            >
              {translate.phrases.banyanApp('View Graphs')}
            </Button>
            <Button
              variant="link"
              onClick={handleTablesClick}
              leftIcon={<FontAwesomeIcon icon={faTableList} color={colors.primary} />}
            >
              {translate.phrases.banyanApp('View Tables')}
            </Button>
            <div css={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <MenuWithVisibility
                columns={columns}
                visibleColumns={visibleColumns}
                onToggle={handleToggleColumn}
                onToggleAll={handleViewAll}
              />
            </div>
          </Center>
        </>
      ) : (
        <Stack gap={0}>
          <Accordion.Control>{propertyName}</Accordion.Control>
          <Group ml={12}>
            <Button
              variant="link"
              onClick={handleGraphsClick}
              leftIcon={<FontAwesomeIcon icon={faChartLine} color={colors.primary} />}
            >
              {translate.phrases.banyanApp('View Graphs')}
            </Button>
            <Button
              variant="link"
              onClick={handleTablesClick}
              leftIcon={<FontAwesomeIcon icon={faTableList} color={colors.primary} />}
            >
              {translate.phrases.banyanApp('View Tables')}
            </Button>
            <div css={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <MenuWithVisibility
                columns={columns}
                visibleColumns={visibleColumns}
                onToggle={handleToggleColumn}
                onToggleAll={handleViewAll}
              />
            </div>
          </Group>
        </Stack>
      )}
      <Accordion.Panel>{children}</Accordion.Panel>
    </Accordion.Item>
  )
}

const MemoizedPropertyAccordion = memo(PropertyAccordion, isEqual) as typeof PropertyAccordion

export { MemoizedPropertyAccordion as PropertyAccordion }
